import React, { useState, useEffect } from 'react';
import { userAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, Plus, Search, Mail, Phone, Shield, UserCheck, UserX,
  MoreVertical, Edit, Trash2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const TeamManagementPage = () => {
  const { user, hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'sales_team',
  });

  const isBuilderAdmin = hasRole(['builder_admin']);
  const isPartner = hasRole(['partner']);

  // Available roles based on current user
  const availableRoles = isPartner 
    ? [{ value: 'sub_user', label: 'Team Member' }]
    : [
        { value: 'builder_admin', label: 'Builder Admin' },
        { value: 'sales_team', label: 'Sales Team' },
        { value: 'marketing_team', label: 'Marketing Team' },
      ];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      let response;
      if (isPartner) {
        // Partners should use sub-users endpoint
        response = await userAPI.listSubUsers();
      } else {
        // Builder admins can use the main users endpoint
        response = await userAPI.list();
      }
      // Ensure we always have an array
      setUsers(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load team members:', error);
      // If 403, show a more specific message
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view team members');
      } else {
        toast.error('Failed to load team members');
      }
      // Set empty array on error to prevent blank page
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (isPartner) {
        // Partners should use sub-users endpoint
        await userAPI.createSubUser(newUser);
      } else {
        // Builder admins can use the main users endpoint
        await userAPI.create(newUser);
      }
      toast.success('Team member added successfully');
      setDialogOpen(false);
      setNewUser({ name: '', email: '', phone: '', password: '', role: 'sales_team' });
      loadUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to add team members');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add team member');
      }
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await userAPI.toggleStatus(userId, !currentStatus);
      toast.success(`User ${currentStatus ? 'disabled' : 'enabled'}`);
      loadUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to update user status');
      } else {
        toast.error('Failed to update user status');
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) {
      return;
    }
    try {
      if (isPartner) {
        // Partners should use sub-users delete endpoint
        await userAPI.deleteSubUser(userId);
      } else {
        // For builder admins, we might need a different endpoint
        // For now, show an error if trying to delete non-sub-users
        toast.error('Delete functionality for builder admin users is not available');
        return;
      }
      toast.success('Team member removed successfully');
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to remove this team member');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to remove team member');
      }
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      'super_admin': 'bg-purple-100 text-purple-700',
      'builder_admin': 'bg-blue-100 text-blue-700',
      'sales_team': 'bg-emerald-100 text-emerald-700',
      'marketing_team': 'bg-orange-100 text-orange-700',
      'partner': 'bg-amber-100 text-amber-700',
      'sub_user': 'bg-slate-100 text-slate-700',
    };
    const labels = {
      'super_admin': 'Super Admin',
      'builder_admin': 'Builder Admin',
      'sales_team': 'Sales Team',
      'marketing_team': 'Marketing',
      'partner': 'Partner',
      'sub_user': 'Team Member',
    };
    return (
      <Badge className={styles[role] || 'bg-gray-100 text-gray-700'}>
        {labels[role] || role}
      </Badge>
    );
  };

  const usersArray = Array.isArray(users) ? users : [];
  const filteredUsers = usersArray.filter((u) => {
    // Don't show current user in the list
    if (u.id === user?.id) return false;
    
    const matchesSearch = 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.phone?.includes(searchTerm);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    
    // Partners only see their sub-users
    if (isPartner) {
      return u.role === 'sub_user' && matchesSearch;
    }
    
    return matchesSearch && matchesRole;
  });

  const stats = {
    total: filteredUsers.length,
    active: filteredUsers.filter(u => u.is_active !== false).length,
    inactive: filteredUsers.filter(u => u.is_active === false).length,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{isPartner ? 'My Team' : 'Team Management'}</h1>
          <p className="text-muted-foreground">
            {isPartner ? 'Manage your sales staff and telecallers' : 'Manage users and permissions'}
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-member">
              <Plus className="h-4 w-4 mr-2" />
              Add {isPartner ? 'Team Member' : 'User'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add {isPartner ? 'Team Member' : 'User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} placeholder="+91 9876543210" />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required />
              </div>
              {!isPartner && (
                <div className="space-y-2">
                  <Label>Role *</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {availableRoles.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button type="submit" className="w-full">Add {isPartner ? 'Team Member' : 'User'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">Total Members</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <UserCheck className="h-4 w-4" />
              <span className="text-sm">Active</span>
            </div>
            <p className="text-2xl font-bold">{stats.active}</p>
          </CardContent>
        </Card>
        <Card className="summary-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <UserX className="h-4 w-4" />
              <span className="text-sm">Inactive</span>
            </div>
            <p className="text-2xl font-bold">{stats.inactive}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name, email or phone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        {!isPartner && (
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]">
              <Shield className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {availableRoles.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No team members found</p>
            <p className="text-muted-foreground">Add your first team member to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map((member) => (
            <Card key={member.id} className="summary-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className={member.is_active !== false ? 'bg-slate-100' : 'bg-red-50'}>
                        {member.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{member.name}</h3>
                        {getRoleBadge(member.role)}
                        {member.is_active === false && (
                          <Badge variant="outline" className="text-red-600 border-red-200">Inactive</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        {member.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {member.email}
                          </span>
                        )}
                        {member.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {member.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Active</span>
                      <Switch 
                        checked={member.is_active !== false}
                        onCheckedChange={() => handleToggleStatus(member.id, member.is_active !== false)}
                      />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => handleDeleteUser(member.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamManagementPage;
